import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const PHASE_ROUTES = {
  0: "/iftarExchangeNoPhase",
  1: "/iftarExchangePollingHome",
  2: "/iftarExchangeHome",
};

/**
 * Polls the phase status every 3s.
 * On first load, optionally redirects if not already on the correct page.
 * On phase change, always redirects to the correct page.
 *
 * @param {object} options
 * @param {number} options.currentPage - The phase this page "belongs to" (0, 1, or 2).
 *                                       If the current phase doesn't match, redirect immediately.
 * @param {boolean} options.redirectOnLoad - Whether to redirect on mount if phase doesn't match. Default true.
 */
export default function usePhaseRedirect({ currentPage, redirectOnLoad = true } = {}) {
  const navigate = useNavigate();
  const previousPhaseRef = useRef(null);

  useEffect(() => {
    const fetchAndCheck = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetPhaseStatus`);
        const data = await res.json();
        const phase = Number(
          Array.isArray(data) ? data[0]?.phase_status : data?.phase_status ?? 0
        );

        const isFirstLoad = previousPhaseRef.current === null;
        const phaseChanged = !isFirstLoad && previousPhaseRef.current !== phase;

        previousPhaseRef.current = phase;

        // On first load: redirect if page doesn't match current phase (and redirectOnLoad is true)
        if (isFirstLoad && redirectOnLoad && currentPage !== undefined && phase !== currentPage) {
          navigate(PHASE_ROUTES[phase]);
          return;
        }

        // On phase change: always redirect
        if (phaseChanged) {
          navigate(PHASE_ROUTES[phase]);
        }
      } catch (err) {
        console.error("usePhaseRedirect: failed to fetch phase", err);
      }
    };

    fetchAndCheck();
    const interval = setInterval(fetchAndCheck, 3000);
    return () => clearInterval(interval);
  }, [navigate, currentPage, redirectOnLoad]);
}